/* SpawnAnything — disasters (client): the "Katastrophen" group in the tool belt (key 0 cycles, outside the nine digit
   slots), input (drag sets the tornado's direction) and the effects for the sim events EV_DISASTER / EV_LAVA.
   Simulation: src/sim/features/disaster.js. Everything here is presentation only (Math.random is fine). */
import * as THREE from 'three';
import './disaster.css';
import {
  DISASTERS, EV_DISASTER, EV_LAVA, KIND_METEOR, LAVA_CELL, LAVA_MOLTEN, LAVA_NONE,
  DZ_TORNADO, DZ_HOLE, DZ_METEOR, DZ_COLLAPSE, DZ_END, DZ_RESYNC,
} from '../sim/features/disaster.js';

/** Tool belt entries (joined into main.js TOOLS after TOOL_KEYS, so the digit keys stay as they are). aoe = cursor ring. */
export const DISASTER_TOOLS = {
  disaster_tornado: { name: 'Tornado', k: 'tornado', aoe: DISASTERS.tornado.reach, hint: 'Klicken setzt ihn ab, Ziehen gibt die Richtung vor' },
  disaster_meteore: { name: 'Meteoritenschauer', k: 'meteore', aoe: DISASTERS.meteore.radius, hint: 'Ein paar Sekunden lang Einschläge im Zielgebiet' },
  disaster_loch: { name: 'Schwarzes Loch', k: 'loch', aoe: DISASTERS.loch.reach, hint: 'Saugt alles ein und kollabiert in einer Druckwelle' },
  disaster_lava: { name: 'Lava-Flut', k: 'lava', aoe: 6, hint: 'Fließt bergab, verbrennt alles und erkaltet zu Stein' },
};
const KEYS = Object.keys(DISASTER_TOOLS);
const FIRE1 = [1.6, 1.0, 0.35], FIRE2 = [1.5, 0.5, 0.12], SMOKE = [0.42, 0.42, 0.44], DUST = [0.62, 0.56, 0.46];
const rnd = Math.random, TAU = Math.PI * 2;
const LMAX = 8000, MMAX = 128;

const NOISE = `
float dzH(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float dzN(vec2 p){ vec2 i = floor(p), f = fract(p); f = f*f*(3.0-2.0*f);
  return mix(mix(dzH(i), dzH(i+vec2(1.0,0.0)), f.x), mix(dzH(i+vec2(0.0,1.0)), dzH(i+vec2(1.0,1.0)), f.x), f.y); }
`;
const TAIL = '#include <tonemapping_fragment>\n#include <colorspace_fragment>\n}';

/**
 * o: { scene, camera, canvas, uGTime, groundAt(cx, cy, out), groundH(x, z), shake(a, x, z), toast(msg), setTool(k),
 *      tool(), active() (god mode in battle), gen(), parts() → { fx, debris }, units() → { b, n }, send(command) }
 */
export function createDisasterFx(o) {
  const { scene, camera, canvas, uGTime } = o;
  const hit = new THREE.Vector3(), hit2 = new THREE.Vector3();
  let curGen = o.gen(), lastGt = uGTime.value;
  const qm = () => Math.min(1.6, Math.max(0.35, o.parts().fx.max / 5000));

  /* ---------------- tool belt group ---------------- */
  const bar = document.getElementById('tools');
  const th = document.createElement('span'); th.className = 'th'; th.textContent = 'Katastrophen';
  const grp = document.createElement('div'); grp.className = 'disaster-grp';
  grp.innerHTML = `<button class="disaster-open" aria-expanded="false" aria-pressed="false" title="Taste 0 wechselt zwischen den Katastrophen"><b>0</b><span>Katastrophen</span><i aria-hidden="true">▸</i></button>
<div class="disaster-fly" role="group" aria-label="Katastrophen" hidden>${KEYS.map(k => `<button data-tool="${k}" aria-pressed="false"><span>${DISASTER_TOOLS[k].name}</span><small>${DISASTER_TOOLS[k].hint}</small></button>`).join('')}</div>`;
  const heads = bar ? bar.querySelectorAll('.th') : [];
  if (bar) { const at = heads[1] || null; bar.insertBefore(th, at); bar.insertBefore(grp, at); }
  const openBtn = grp.querySelector('.disaster-open'), fly = grp.querySelector('.disaster-fly'), label = openBtn.querySelector('span');
  let lastPick = KEYS[0], shownTool = '';
  const setOpen = on => { fly.hidden = !on; openBtn.setAttribute('aria-expanded', String(on)); };
  const pick = k => { lastPick = k; o.setTool(k); setOpen(false); if (o.tool() === k) o.toast(`${DISASTER_TOOLS[k].name}: ${DISASTER_TOOLS[k].hint}`); };
  openBtn.addEventListener('click', () => setOpen(fly.hidden));
  fly.querySelectorAll('[data-tool]').forEach(b => b.addEventListener('click', () => pick(b.dataset.tool)));
  document.addEventListener('pointerdown', e => { if (!fly.hidden && !grp.contains(e.target)) setOpen(false); });
  addEventListener('keydown', e => {
    if (e.code !== 'Digit0' || e.repeat || !o.active() || (e.target.matches && e.target.matches('input,select,textarea'))) return;
    const i = KEYS.indexOf(o.tool());
    pick(i < 0 ? lastPick : KEYS[(i + 1) % KEYS.length]);
  });
  function syncButton() {
    const t = o.tool(); if (t === shownTool) return; shownTool = t;
    const D = DISASTER_TOOLS[t];
    label.textContent = D ? D.name : 'Katastrophen';
    openBtn.setAttribute('aria-pressed', String(!!D));
  }

  /* ---------------- input: click = disaster at the cursor, drag (tornado) = direction ---------------- */
  // feature command { t: 'dz', k, x, z, dx, dz } (positions in 1/16 m, direction * 127): single player and lockstep
  function send(k, x, z, dx = 0, dz = 0) {
    o.send({ t: 'dz', k, x: Math.round(x * 16), z: Math.round(z * 16), dx: Math.round(dx * 127), dz: Math.round(dz * 127) });
  }
  /** main.js useTool hook: true when the tool was a disaster (handled here). */
  function use(tool, cx, cy) {
    const D = DISASTER_TOOLS[tool]; if (!D) return false;
    if (o.groundAt(cx, cy, hit)) send(D.k, hit.x, hit.z);
    return true;
  }
  const arrowMat = new THREE.MeshBasicMaterial({ color: 0xffa040, transparent: true, opacity: 0.85, depthWrite: false });
  const arrow = new THREE.Group();
  const shaft = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.12, 1), arrowMat); shaft.position.z = 0.5;
  const headGeo = new THREE.ConeGeometry(1.4, 2.6, 4); headGeo.rotateX(Math.PI / 2);
  const head = new THREE.Mesh(headGeo, arrowMat);
  arrow.add(shaft, head); arrow.visible = false; arrow.renderOrder = 2; scene.add(arrow);
  let drag = null;
  function aim(e) {
    if (!o.groundAt(e.clientX, e.clientY, hit2)) return;
    const dx = hit2.x - drag.x, dz = hit2.z - drag.z, L = Math.hypot(dx, dz);
    drag.dx = dx; drag.dz = dz;
    arrow.visible = Math.hypot(e.clientX - drag.sx, e.clientY - drag.sy) > 8 && L > 0.5;
    if (!arrow.visible) return;
    arrow.position.set(drag.x, drag.y + 0.3, drag.z); arrow.rotation.set(0, Math.atan2(dx, dz), 0);
    shaft.scale.z = Math.max(0.1, L - 2.4); head.position.z = Math.max(0.1, L - 2.4) + 1.3;
  }
  addEventListener('pointerdown', e => {
    if (e.target !== canvas || e.pointerType === 'touch' || e.button !== 0 || o.tool() !== 'disaster_tornado' || !o.active()) return;
    if (!o.groundAt(e.clientX, e.clientY, hit)) return;
    e.stopImmediatePropagation(); // the drag aims the tornado instead of turning the camera
    try { canvas.setPointerCapture(e.pointerId); } catch { /* pointer already gone */ }
    drag = { id: e.pointerId, sx: e.clientX, sy: e.clientY, x: hit.x, y: hit.y, z: hit.z, dx: 0, dz: 0 };
  }, true);
  addEventListener('pointermove', e => { if (drag && e.pointerId === drag.id) aim(e); }, true);
  const endDrag = (e, cancel) => {
    if (!drag || e.pointerId !== drag.id) return;
    e.stopImmediatePropagation();
    aim(e);
    const L = Math.hypot(drag.dx, drag.dz), dirOK = arrow.visible && L > 0.5;
    if (!cancel) send('tornado', drag.x, drag.z, dirOK ? drag.dx / L : 0, dirOK ? drag.dz / L : 0);
    drag = null; arrow.visible = false;
  };
  addEventListener('pointerup', e => endDrag(e, false), true);
  addEventListener('pointercancel', e => endDrag(e, true), true);

  /* ---------------- tornado: swirling funnel (shader) + dust and debris ---------------- */
  const FH = 26;
  const funnelGeo = new THREE.CylinderGeometry(7.5, 0.8, FH, 32, 12, true); funnelGeo.translate(0, FH / 2, 0);
  const funnelMat = () => new THREE.ShaderMaterial({
    uniforms: { uT: { value: 0 }, uS: { value: 0 } }, transparent: true, depthWrite: false, side: THREE.DoubleSide,
    vertexShader: `uniform float uT; varying vec2 vUv; varying float vH;
void main(){ vUv = uv; vec3 p = position; float h = p.y;
  p.x += sin(h*0.12 - uT*1.7) * h * 0.05 + sin(h*0.31 + uT*2.3) * 0.3; p.z += cos(h*0.1 - uT*1.3) * h * 0.04;
  vH = h / ${FH.toFixed(1)}; gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0); }`,
    fragmentShader: `uniform float uT; uniform float uS; varying vec2 vUv; varying float vH;${NOISE}
void main(){
  float s1 = 0.5 + 0.5*sin(vUv.x*31.4159 + vUv.y*14.0 - uT*7.0), s2 = 0.5 + 0.5*sin(vUv.x*18.8496 - vUv.y*9.0 - uT*4.3 + 1.7);
  float n = dzN(vec2(vUv.y*10.0 - uT*1.5, uT*0.7));
  float a = (0.16 + 0.45*s1*s2 + 0.15*n) * smoothstep(0.0, 0.1, vH) * (1.0 - smoothstep(0.72, 1.0, vH)) * uS;
  gl_FragColor = vec4(mix(vec3(0.3, 0.27, 0.23), vec3(0.74, 0.68, 0.58), s1*0.6 + n*0.4), a);
${TAIL}`,
  });
  const tornados = new Map();
  function tornadoEv(id, x, z, s) {
    let T = tornados.get(id);
    if (!T) { T = { x, z, tx: x, tz: z, s: 0, ts: s, end: false, last: 0, acc: 0, mesh: new THREE.Mesh(funnelGeo, funnelMat()) }; T.mesh.frustumCulled = false; scene.add(T.mesh); tornados.set(id, T); }
    T.tx = x; T.tz = z; T.ts = s; T.last = uGTime.value;
  }
  function dropTornado(id, T) { scene.remove(T.mesh); T.mesh.material.dispose(); tornados.delete(id); }
  function stepTornados(dt, dtg, gt) {
    const { fx, debris } = o.parts(), rate = 260 * qm();
    for (const [id, T] of tornados) {
      if (gt - T.last > 2) T.end = true; // missed its end event (e.g. a new battle)
      const k = Math.min(1, dt * 10); T.x += (T.tx - T.x) * k; T.z += (T.tz - T.z) * k;
      T.s += ((T.end ? 0 : T.ts) - T.s) * Math.min(1, dt * 4);
      if (T.end && T.s < 0.02) { dropTornado(id, T); continue; }
      const g = o.groundH(T.x, T.z), s = T.s;
      T.mesh.position.set(T.x, g, T.z); T.mesh.scale.set(0.6 + 0.4 * s, 0.4 + 0.6 * s, 0.6 + 0.4 * s);
      T.mesh.material.uniforms.uT.value = gt; T.mesh.material.uniforms.uS.value = s;
      if (!dtg) continue;
      T.acc += rate * s * dtg;
      for (; T.acc >= 1; T.acc--) {
        const h = Math.pow(rnd(), 1.3) * FH * 0.85, r = (0.8 + h * 0.26) * (0.6 + 0.4 * s), a = rnd() * TAU, c = Math.cos(a), sn = Math.sin(a), sp = 5 + h * 0.4;
        const d = 0.75 + rnd() * 0.35;
        fx.spawn(T.x + c * r, g + h, T.z + sn * r, -sn * sp, 1 + rnd() * 2, c * sp, 0.45 + rnd() * 0.4, 0.4 + rnd() * 0.6 + h * 0.03, 0, 3, 1, DUST[0] * d, DUST[1] * d, DUST[2] * d);
        if (rnd() < 0.18) { // ground dust ring
          const rr = 2 + rnd() * 4;
          fx.spawn(T.x + c * rr, g + 0.3, T.z + sn * rr, -sn * 4 + c * 2, 0.6, c * 4 + sn * 2, 1 + rnd() * 0.8, 1.6 + rnd() * 1.6, 0, 1.2, 2, DUST[0], DUST[1], DUST[2]);
        }
        if (rnd() < 0.08) { // flung debris
          const out = 4 + rnd() * 6;
          debris.spawn(T.x + c * 2, g + 1 + rnd() * 6, T.z + sn * 2, c * out - sn * 6, 6 + rnd() * 10, sn * out + c * 6, 6, 0.15 + rnd() * 0.25, 18, 6, 0, 0.36, 0.28, 0.18, g);
        }
      }
      if (s > 0.3) o.shake(0.9 * s, T.x, T.z);
    }
  }

  /* ---------------- black hole: dark core, accretion disk, Einstein ring, infalling dust ---------------- */
  const coreGeo = new THREE.SphereGeometry(1, 32, 16), coreMat = new THREE.MeshBasicMaterial({ color: 0x000000 });
  const diskGeo = new THREE.RingGeometry(1.4, 5.2, 72, 1);
  const haloGeo = new THREE.PlaneGeometry(1, 1);
  const diskMat = () => new THREE.ShaderMaterial({
    uniforms: { uT: { value: 0 }, uS: { value: 0 } }, transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
    vertexShader: 'varying vec2 vP; void main(){ vP = position.xy; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader: `uniform float uT; uniform float uS; varying vec2 vP;${NOISE}
void main(){
  float r = length(vP), a = atan(vP.y, vP.x), t = clamp((r - 1.4) / 3.8, 0.0, 1.0);
  float sw = 0.5 + 0.5*sin(a*3.0 + 9.0/r - uT*5.0), n = dzN(vec2(a*4.0 + 12.0/r - uT*3.0, r*1.5));
  vec3 c = mix(vec3(2.4, 2.0, 1.6), vec3(1.5, 0.45, 0.08), sqrt(t)) * (0.45 + 0.55*sw*0.6 + 0.4*n);
  float al = pow(1.0 - t, 1.6) * smoothstep(0.0, 0.06, t) * uS;
  gl_FragColor = vec4(c, al);
${TAIL}`,
  });
  const haloMat = () => new THREE.ShaderMaterial({
    uniforms: { uT: { value: 0 }, uS: { value: 0 } }, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
    fragmentShader: `uniform float uT; uniform float uS; varying vec2 vUv;
void main(){
  float r = length(vUv - 0.5) * 2.0;
  float ring = exp(-pow((r - 0.3) / 0.035, 2.0)) * (0.8 + 0.2*sin(uT*9.0));
  float glow = exp(-max(r - 0.3, 0.0) * 6.0) * step(0.28, r) * 0.45;
  vec3 c = vec3(1.4, 1.5, 2.2) * ring + vec3(0.7, 0.3, 1.1) * glow;
  float al = (ring + glow) * uS * (1.0 - smoothstep(0.85, 1.0, r));
  gl_FragColor = vec4(c, al);
${TAIL}`,
  });
  const holes = new Map(), flashes = [];
  function holeEv(id, x, z, s, y) {
    let H = holes.get(id);
    if (!H) {
      const g = new THREE.Group(), core = new THREE.Mesh(coreGeo, coreMat), disk = new THREE.Mesh(diskGeo, diskMat()), halo = new THREE.Mesh(haloGeo, haloMat());
      disk.rotation.set(-Math.PI / 2 + 0.35, 0, 0.2); halo.renderOrder = 3; disk.renderOrder = 3;
      g.add(core, disk); scene.add(g, halo); g.traverse(m => { m.frustumCulled = false; });
      H = { x, z, y, s: 0, ts: s, last: 0, acc: 0, g, core, disk, halo }; holes.set(id, H);
    }
    H.x = x; H.z = z; H.y = y; H.ts = s; H.last = uGTime.value;
  }
  function dropHole(id, H) { scene.remove(H.g, H.halo); H.disk.material.dispose(); H.halo.material.dispose(); holes.delete(id); }
  function stepHoles(dt, dtg, gt) {
    const { fx } = o.parts(), rate = 200 * qm(), R = DISASTERS.loch.reach;
    for (const [id, H] of holes) {
      if (gt - H.last > 2) { dropHole(id, H); continue; }
      H.s += (H.ts - H.s) * Math.min(1, dt * 5);
      const s = H.s;
      H.g.position.set(H.x, H.y, H.z); H.core.scale.setScalar(0.3 + 2.1 * s); H.disk.scale.setScalar(0.4 + 1.1 * s);
      H.disk.rotation.z = gt * 0.6;
      H.halo.position.set(H.x, H.y, H.z); H.halo.quaternion.copy(camera.quaternion); H.halo.scale.setScalar(16 * s + 0.1);
      for (const m of [H.disk, H.halo]) { m.material.uniforms.uT.value = gt; m.material.uniforms.uS.value = s; }
      if (!dtg) continue;
      H.acc += rate * s * dtg;
      for (; H.acc >= 1; H.acc--) { // dust drawn in: the drift of kind-1 particles ends at p0 + v / 2.2, i.e. in the centre
        const a = rnd() * TAU, r = 5 + rnd() * R * 0.85, px = H.x + Math.cos(a) * r, pz = H.z + Math.sin(a) * r, py = o.groundH(px, pz) + 0.3 + rnd() * 2;
        const glow = rnd() < 0.35, life = 0.9 + rnd() * 0.5;
        fx.spawn(px, py, pz, (H.x - px) * 2.4 - Math.sin(a) * r * 0.5, (H.y - py) * 2.4, (H.z - pz) * 2.4 + Math.cos(a) * r * 0.5, life, 0.35 + rnd() * 0.5, 0, 4, 1,
          ...(glow ? [1.3, 0.6, 1.8] : DUST));
      }
      o.shake(1.1 * s, H.x, H.z);
    }
  }
  function collapse(id, x, z, R, y) {
    const H = holes.get(id); if (H) dropHole(id, H);
    const { fx, debris } = o.parts(), g = o.groundH(x, z), m = qm();
    for (let i = 0, n = 150 * m | 0; i < n; i++) { const a = (i / n) * TAU, sp = R * (1.1 + rnd() * 0.4); fx.spawn(x, g + 0.5, z, Math.cos(a) * sp, 0.6, Math.sin(a) * sp, 1.2 + rnd() * 0.6, 1.6 + rnd() * 2.2, 0, 1.5, 2, DUST[0], DUST[1], DUST[2]); }
    for (let i = 0, n = 110 * m | 0; i < n; i++) {
      const a = rnd() * TAU, el = (rnd() - 0.3) * 1.2, sp = 18 + rnd() * 30;
      fx.spawn(x, y, z, Math.cos(a) * Math.cos(el) * sp, Math.sin(el) * sp, Math.sin(a) * Math.cos(el) * sp, 0.5 + rnd() * 0.5, 0.4 + rnd() * 0.6, 0, 6, 1, ...(rnd() < 0.5 ? [1.6, 0.8, 2.2] : [2.2, 2.0, 2.4]));
    }
    for (let i = 0; i < 60; i++) { const a = rnd() * TAU, sp = 6 + rnd() * 16; debris.spawn(x, g + 0.4, z, Math.cos(a) * sp, 6 + rnd() * 12, Math.sin(a) * sp, 10, 0.2 + rnd() * 0.4, 18, 6, 0, 0.3, 0.27, 0.3, g); }
    const fm = new THREE.Mesh(coreGeo, new THREE.MeshBasicMaterial({ color: new THREE.Color(2.2, 1.8, 2.6), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
    fm.position.set(x, y, z); fm.frustumCulled = false; scene.add(fm); flashes.push({ m: fm, t0: performance.now(), R });
    o.shake(R * 2.2, x, z);
  }
  function stepFlashes(now) {
    for (let k = flashes.length - 1; k >= 0; k--) {
      const F = flashes[k], f = (now - F.t0) / 450;
      if (f >= 1) { scene.remove(F.m); F.m.material.dispose(); flashes.splice(k, 1); continue; }
      F.m.scale.setScalar(2 + F.R * 0.7 * Math.sqrt(f)); F.m.material.opacity = 1 - f;
    }
  }

  /* ---------------- meteors: glowing rock + fire trail on the way down (impact = sim explosion kind 36) ---------------- */
  const metMesh = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(1, 0), new THREE.MeshBasicMaterial({ color: new THREE.Color(2.4, 1.3, 0.5) }), MMAX);
  metMesh.count = 0; metMesh.frustumCulled = false; metMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage); scene.add(metMesh);
  const meteors = [], _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _e = new THREE.Euler();
  function meteorEv(x, z, fall, h) {
    if (meteors.length >= MMAX) return;
    const g = o.groundH(x, z);
    meteors.push({ x, z, g, sx: x - Math.sin(h) * 50, sy: g + 85, sz: z - Math.cos(h) * 50, t0: uGTime.value, fall, acc: 0, rot: rnd() * TAU });
  }
  function stepMeteors(dtg, gt) {
    const { fx } = o.parts(), m = qm();
    let n = 0;
    for (let k = 0; k < meteors.length; k++) {
      const M = meteors[k], p = (gt - M.t0) / M.fall;
      if (p >= 1) continue;
      const x = M.sx + (M.x - M.sx) * p, y = M.sy + (M.g - M.sy) * p, z = M.sz + (M.z - M.sz) * p;
      _m.compose(_p.set(x, y, z), _q.setFromEuler(_e.set(gt * 3 + M.rot, M.rot, 0)), _s.setScalar(1.1)); metMesh.setMatrixAt(n++, _m);
      M.acc += 140 * m * dtg;
      const vx = (M.x - M.sx) / M.fall, vy = (M.g - M.sy) / M.fall, vz = (M.z - M.sz) / M.fall;
      for (; M.acc >= 1; M.acc--) {
        const b = rnd() * 0.6;
        if (rnd() < 0.75) fx.spawn(x - vx * b * 0.03, y - vy * b * 0.03, z - vz * b * 0.03, -vx * 0.08 + (rnd() - 0.5) * 2, -vy * 0.08 + rnd(), -vz * 0.08 + (rnd() - 0.5) * 2, 0.25 + rnd() * 0.2, 1 + rnd() * 1.1, 0, 4, 1, ...(rnd() < 0.5 ? FIRE1 : FIRE2));
        else fx.spawn(x, y, z, (rnd() - 0.5) * 1.5, rnd(), (rnd() - 0.5) * 1.5, 1.4 + rnd(), 1.2 + rnd() * 1.4, 0, 1, 2, SMOKE[0] * 0.7, SMOKE[1] * 0.7, SMOKE[2] * 0.7);
      }
    }
    for (let k = meteors.length - 1; k >= 0; k--) if ((gt - meteors[k].t0) / meteors[k].fall >= 1) meteors.splice(k, 1);
    metMesh.count = n; if (n) metMesh.instanceMatrix.needsUpdate = true;
  }
  function impact(x, y, z, aoe) {
    const { fx } = o.parts();
    for (let i = 0; i < 14; i++) { const a = rnd() * TAU, sp = 4 + rnd() * 8; fx.spawn(x, y + 0.3, z, Math.cos(a) * sp, 6 + rnd() * 8, Math.sin(a) * sp, 0.9 + rnd() * 0.6, 0.18 + rnd() * 0.15, 9, 8, 3, ...FIRE1); }
    o.shake(aoe * 0.9, x, z);
  }

  /* ---------------- lava: one instanced box per grid cell, molten glow + crust in the shader ---------------- */
  const lavaGeo = new THREE.BoxGeometry(1, 1, 1);
  const aLv = new THREE.InstancedBufferAttribute(new Float32Array(LMAX * 2), 2); aLv.setUsage(THREE.DynamicDrawUsage);
  lavaGeo.setAttribute('aLv', aLv);
  const lavaMat = new THREE.MeshLambertMaterial({ color: 0xffffff });
  lavaMat.onBeforeCompile = sh => {
    sh.uniforms.uGT = uGTime;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec2 aLv; varying vec2 vLv; varying vec3 vLW;')
      .replace('#include <project_vertex>', '#include <project_vertex>\nvLv = aLv; vLW = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nuniform float uGT; varying vec2 vLv; varying vec3 vLW;' + NOISE)
      .replace('#include <color_fragment>', `#include <color_fragment>
  float lavaN = dzN(vLW.xz * 0.55 + vec2(uGT * 0.15, -uGT * 0.1)) * 0.6 + dzN(vLW.xz * 1.7 - uGT * 0.3) * 0.4;
  float heat = vLv.x < 1.5 ? 1.0 : 1.0 - clamp((uGT - vLv.y) / 3.0, 0.0, 1.0);
  float crust = smoothstep(0.45, 0.66, lavaN);
  diffuseColor.rgb = mix(vec3(0.13, 0.12, 0.12) * (0.75 + 0.5 * lavaN), vec3(0.28, 0.08, 0.03), heat * (1.0 - crust));
  vec3 lavaGlow = mix(vec3(1.9, 0.5, 0.06), vec3(2.6, 1.5, 0.35), lavaN) * heat * (1.0 - crust * 0.85);`)
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += lavaGlow;');
  };
  lavaMat.customProgramCacheKey = () => 'dzlava';
  const lavaMesh = new THREE.InstancedMesh(lavaGeo, lavaMat, LMAX);
  lavaMesh.count = 0; lavaMesh.frustumCulled = false; lavaMesh.receiveShadow = true; lavaMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  scene.add(lavaMesh);
  const lavaSlot = new Map(), LST = new Uint8Array(LMAX), LP = new Float32Array(LMAX * 3);
  let lUsed = 0, lMolten = 0, lLo = Infinity, lHi = -1, burnT = 0;
  const keyOf = (x, z) => Math.floor(x / LAVA_CELL) * 131072 + Math.floor(z / LAVA_CELL);
  function lavaEv(x, y, z, st) {
    const key = keyOf(x, z);
    let i = lavaSlot.get(key);
    if (i === undefined) { if (st === LAVA_NONE || lUsed >= LMAX) return; i = lUsed++; lavaSlot.set(key, i); LST[i] = LAVA_NONE; }
    if ((LST[i] === LAVA_MOLTEN) !== (st === LAVA_MOLTEN)) lMolten += st === LAVA_MOLTEN ? 1 : -1;
    if (st !== LST[i]) { aLv.array[i * 2] = st; aLv.array[i * 2 + 1] = uGTime.value; }
    LST[i] = st; LP[i * 3] = x; LP[i * 3 + 1] = y; LP[i * 3 + 2] = z;
    const depth = 1.6;
    _m.compose(_p.set(x, y + 0.04 - depth / 2, z), _q.identity(), st === LAVA_NONE ? _s.setScalar(0) : _s.set(LAVA_CELL * 1.01, depth, LAVA_CELL * 1.01));
    lavaMesh.setMatrixAt(i, _m);
    if (i < lLo) lLo = i; if (i > lHi) lHi = i;
  }
  function flushLava() {
    lavaMesh.count = lUsed;
    if (lHi < 0) return;
    lavaMesh.instanceMatrix.clearUpdateRanges(); lavaMesh.instanceMatrix.addUpdateRange(lLo * 16, (lHi - lLo + 1) * 16); lavaMesh.instanceMatrix.needsUpdate = true;
    aLv.clearUpdateRanges(); aLv.addUpdateRange(lLo * 2, (lHi - lLo + 1) * 2); aLv.needsUpdate = true;
    lLo = Infinity; lHi = -1;
  }
  function stepLava(dtg, gt) {
    if (!lMolten || !dtg) return;
    const { fx } = o.parts(), m = qm();
    for (let k = 0, n = Math.floor(Math.min(lMolten, 60) * dtg * 2 * m + rnd()); k < n; k++) { // bubbles and smoke over the molten cells
      const i = (rnd() * lUsed) | 0; if (LST[i] !== LAVA_MOLTEN) continue;
      const x = LP[i * 3] + (rnd() - 0.5) * LAVA_CELL, y = LP[i * 3 + 1] + 0.1, z = LP[i * 3 + 2] + (rnd() - 0.5) * LAVA_CELL;
      if (rnd() < 0.6) fx.spawn(x, y, z, (rnd() - 0.5) * 1.5, 2 + rnd() * 3, (rnd() - 0.5) * 1.5, 0.5 + rnd() * 0.4, 0.12 + rnd() * 0.15, 6, 6, 3, ...FIRE1);
      else fx.spawn(x, y, z, (rnd() - 0.5), 0.8 + rnd(), (rnd() - 0.5), 1.6 + rnd(), 0.8 + rnd(), 0, 1, 2, 0.3, 0.28, 0.27);
    }
    if (gt - burnT < 0.25) return;
    burnT = gt;
    const { b, n } = o.units(); if (!b) return;
    let lit = 0;
    for (let i = 0; i < n && lit < 220; i++) { // units standing in molten lava catch fire
      const k = i * 10; if (b[k + 9] !== 1) continue;
      const s = lavaSlot.get(keyOf(b[k], b[k + 2]));
      if (s === undefined || LST[s] !== LAVA_MOLTEN) continue;
      lit++;
      for (let f = 0; f < 3; f++) fx.spawn(b[k] + (rnd() - 0.5) * 0.6, b[k + 1] + 0.3 + rnd() * 1.2, b[k + 2] + (rnd() - 0.5) * 0.6, (rnd() - 0.5), 2 + rnd() * 2, (rnd() - 0.5), 0.35 + rnd() * 0.3, 0.35 + rnd() * 0.35, 0, 4, 1, ...(rnd() < 0.5 ? FIRE1 : FIRE2));
    }
  }

  /* ---------------- events, frame, reset ---------------- */
  function reset() {
    for (const [id, T] of tornados) dropTornado(id, T);
    for (const [id, H] of holes) dropHole(id, H);
    for (const F of flashes) { scene.remove(F.m); F.m.material.dispose(); }
    flashes.length = 0; meteors.length = 0; metMesh.count = 0;
    lavaSlot.clear(); lUsed = 0; lMolten = 0; lLo = Infinity; lHi = -1; lavaMesh.count = 0;
    drag = null; arrow.visible = false;
  }
  function checkGen() { const g = o.gen(); if (g !== curGen) { curGen = g; reset(); } }
  /** main.js handleEvents hook: the whole event buffer of one sim frame. */
  function events(ev, n) {
    checkGen();
    for (let k = 0; k < n; k++) {
      const p = k * 8, t = ev[p];
      if (t === EV_DISASTER) {
        const sub = ev[p + 1], id = ev[p + 2], x = ev[p + 3], z = ev[p + 4];
        if (sub === DZ_TORNADO) tornadoEv(id, x, z, ev[p + 5]);
        else if (sub === DZ_END) { const T = tornados.get(id); if (T) T.end = true; }
        else if (sub === DZ_HOLE) holeEv(id, x, z, ev[p + 5], ev[p + 7]);
        else if (sub === DZ_COLLAPSE) collapse(id, x, z, ev[p + 5], ev[p + 6]);
        else if (sub === DZ_METEOR) meteorEv(x, z, ev[p + 5], ev[p + 6]);
        else if (sub === DZ_RESYNC) reset(); // replay seek: the lava cells follow
      } else if (t === EV_LAVA) lavaEv(ev[p + 2], ev[p + 3], ev[p + 4], ev[p + 5]);
      else if (t === 2 && ev[p + 5] === KIND_METEOR) impact(ev[p + 1], ev[p + 2], ev[p + 3], ev[p + 4]);
    }
    flushLava();
  }
  /** main.js frame hook. */
  function frame(dt) {
    checkGen(); syncButton();
    const gt = uGTime.value, dtg = Math.min(0.1, Math.max(0, gt - lastGt)); lastGt = gt;
    if (tornados.size) stepTornados(dt, dtg, gt);
    if (holes.size) stepHoles(dt, dtg, gt);
    if (flashes.length) stepFlashes(performance.now());
    if (meteors.length || metMesh.count) stepMeteors(dtg, gt);
    if (lMolten) stepLava(dtg, gt);
    const { fx, debris } = o.parts(); fx.flush(); debris.flush();
  }
  return { use, events, frame, reset };
}
